import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import parse, { domToReact } from "html-react-parser";
import { useUser } from "../../../context/UserContext";
import { usePsChat, MESSAGE_SENT } from '../../../context/PsChatMessageContext';
import { useJob } from "../../../context/JobContext";
import { useAgreement } from "../../../context/AgreementContext";
import InlineHtmlEditor from "../../../components/shared-components/ChatMessageContentEditor";
import Dropdown from 'react-dropdown-select';
import TextareaAutosize from "react-textarea-autosize";
import { useMediaQuery } from "react-responsive";
import {
  fetchAvatars,
  useUserRole,
  useUserId,
  truncateJobTitle,
  cleanJobDescription,
  dateToUnixTimestamp,
  formatUnixDate,
  formatUnixFullDate,
  formatUnixFullTime,
  formatUnixShortDate,
  dateStringWithDayNoTime,
  playNotificationSound,
  jobDropdownStyles,
  centeredContentRenderer,
  chatFilterDropdownItemRenderer,
  initials, avatarColorsFor
} from "../../../util_functions/helpers";
import MeetAndGreet from "./MeetAndGreet";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import {
  FaBriefcase,
  FaAngleLeft,
  FaRegCalendarAlt,
  FaPhoneAlt,
  FaVideo,
  FaMicrophone,
  FaSearch,
  FaEllipsisH,
  FaArrowRight,
  FaAngleDown,
  FaThumbtack,
  FaRegArrowAltCircleDown,
  FaSortAmountDownAlt, FaChevronDown,
  FaPaperclip, FaTrashAlt, FaEdit, FaCheck, FaTimes, FaFileAlt, FaPlay, FaFilePdf, FaBan
} from "react-icons/fa";
import { FiMessageSquare, FiMoreVertical, FiTrash2, FiArchive } from "react-icons/fi";
import { BsCircleHalf, BsCircleFill } from "react-icons/bs";
import { MdArrowDownward } from "react-icons/md";
import { PiPaperPlaneRightFill, PiChecksBold } from "react-icons/pi";
import { CiEdit } from "react-icons/ci";
import { IoMdImages } from "react-icons/io";
import { LuListFilter } from "react-icons/lu";
import { TbFilterCog, TbSortDescending } from "react-icons/tb";
import {
  HiBell,
  HiBellSlash,
  HiUserMinus,
  HiFlag,
} from "react-icons/hi2";
import DOMPurify from "dompurify";
import BlockCallsAlert from "./BlockCallsAlert";
import RestrictUserAlert from "./RestrictUserAlert";
import ReportThreadAlert from "./ReportThreadAlert";
import config from "../../../config/config";
import axios from "axios";
import { Button } from "antd";
import { v4 as uuidv4 } from "uuid";

const PSInbox = () => {
  const { workerUser } = useUser();
  const userId = useUserId();
  const userRole = useUserRole();
  const serviceRates = workerUser?.serviceRates || {};
  const availability = workerUser?.availability || {};

  const initialRates = Object.fromEntries(
    Object.entries(serviceRates)
      .filter(([shift]) => shift !== "Initial greeting session")
      .map(([shift, rate]) => [shift, rate || ""])
  );

  const offeredRateTypes = Object.entries(serviceRates)
    .filter(([shift, rate]) => shift !== "Initial greeting session" && Boolean(rate))
    .map(([shift]) => shift);

  console.log("[PSInbox] workerUser() →", workerUser);
  console.log("[PSInbox] userId() →", userId);
  console.log("[PSInbox] userRole() →", userRole);

  const imgUrl = config.PROFILE_IMAGES_URL;
  const defaultAvatar = config.AVATAR_DEFAULT_URL;
  const { state, dispatch, fetchMessages, markReadBulk, fetchUnreadCount, markReadOptimistic, flushPendingReads, pinThread, archiveThread, softDeleteThread, editMessage, pinMessage, softDeleteMessage, blockCalls, blockUser, reportThread, toggleThreadNotifications } = usePsChat();
  const { selectedJob, fetchJobById } = useJob();
  const jobId = selectedJob?.id ?? "";

  const {
    messages,
    selectedMessageId,
    unreadCounts
  } = state;

  const [filterBy, setFilterBy] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [sortDir, setSortDir] = useState("desc");
  const [showActionsFor, setShowActionsFor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const isMessageDeleted = (m) => {
    if (!m) return false;
    return (
      m.messageDeleted === true ||
      m.fullMessage?.messageDeleted === true ||
      m.chatMessage?.messageDeleted === true
    );
  };

  const activeThread = useMemo(
    () => state.messages.find(t => t.id === selectedMessageId) || null,
    [state.messages, selectedMessageId]
  );

  const sortedThreads = useMemo(() => {
    if (!messages) return [];

    const getLastNonDeleted = (t) => {
      const msgs = t.fullMessages || [];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (!isMessageDeleted(msgs[i])) {
          return msgs[i];
        }
      }
      return null;
    };

    const getLastTs = (t) => {
      const last = getLastNonDeleted(t);
      return Number(last?.chatMessageTimeStamp ?? 0);
    };

    const getClientName = (t) => (t.client || "").toLowerCase();
    const getUnread = (t) => unreadCounts[t.id] || 0;

    const baseSort = (a, b) => {
      switch (sortBy) {
        case "unreadFirst": {
          const ua = getUnread(a);
          const ub = getUnread(b);
          if (ub !== ua) return ub - ua;
          return getLastTs(b) - getLastTs(a);
        }
        case "az": {
          const cmp = getClientName(a).localeCompare(getClientName(b));
          return cmp !== 0 ? cmp : getLastTs(b) - getLastTs(a);
        }
        case "za": {
          const cmp = getClientName(b).localeCompare(getClientName(a));
          return cmp !== 0 ? cmp : getLastTs(b) - getLastTs(a);
        }
        case "recent":
        default:
          return getLastTs(b) - getLastTs(a);
      }
    };

    const visible = messages.filter(t => !t.deleted && !t.softDeleted);

    const pinned = visible.filter(t => t.pinned);
    const unpinned = visible.filter(t => !t.pinned);

    pinned.sort(baseSort);
    unpinned.sort(baseSort);

    if (sortDir === "asc") {
      pinned.reverse();
      unpinned.reverse();
    }

    return [...pinned, ...unpinned];
  }, [messages, unreadCounts, sortBy, sortDir]);


  const visibleMessages = useMemo(() => {
    if (!activeThread) return [];

    const msgs = activeThread.fullMessages ?? [];

    const cleaned = msgs.filter(m => !isMessageDeleted(m));

    const pinned = cleaned.filter(m => m.pinned);
    const others = cleaned.filter(m => !m.pinned);

    const sortByTs = (a, b) =>
      Number(a.chatMessageTimeStamp ?? 0) - Number(b.chatMessageTimeStamp ?? 0);

    pinned.sort(sortByTs);
    others.sort(sortByTs);

    return [...pinned, ...others];
  }, [activeThread]);

  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Unread", value: "unread" },
    { label: "Read", value: "read" },
    { label: "Archived", value: "archived" },
  ];


  const sortOptions = [
    { label: "Recent", value: "recent" },
    { label: "Unread first", value: "unreadFirst" },
    { label: "Client A–Z", value: "az" },
    { label: "Client Z–A", value: "za" },
  ];

  const { visibleThreads, searchHits } = useMemo(() => {
    let list = sortedThreads;

    if (filterBy === "unread") {
      list = list.filter(t => (unreadCounts[t.id] || 0) > 0);
    } else if (filterBy === "read") {
      list = list.filter(t => (unreadCounts[t.id] || 0) === 0);
    } else if (filterBy === "archived") {
      list = list.filter(t => t.chatArchived === true);
    }

    const qRaw = searchTerm.trim();
    const q = qRaw.toLowerCase();
    const hitsMap = new Map();

    if (!q) {
      return { visibleThreads: list, searchHits: hitsMap };
    }

    const filtered = list.filter((t) => {
      const participantName =
        userRole === "worker"
          ? (t.client || "")
          : (t.worker || "");

      const nameMatch = participantName.toLowerCase().includes(q);

      const messages = (t.fullMessages || []).filter(m => !isMessageDeleted(m));
      const msgHits = messages.filter(m =>
        (m.text || "").toLowerCase().includes(q)
      );

      const latestText =
        t.latestMessage ||
        messages[messages.length - 1]?.text ||
        "";

      const latestMatch = latestText.toLowerCase().includes(q);

      const keep = nameMatch || latestMatch || msgHits.length > 0;
      if (!keep) return false;

      if (msgHits.length > 0) {
        const primary = msgHits[msgHits.length - 1];

        hitsMap.set(t.id, {
          primary,
          all: msgHits,
        });
      }

      return true;
    });

    return { visibleThreads: filtered, searchHits: hitsMap };
  }, [sortedThreads, filterBy, unreadCounts, searchTerm, userRole]);

  const { messageId } = useParams();
  const [text, setText] = useState("");
  const [chatInfoPane, setChatInfoPane] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAgreementSigned, setIsAgreementSigned] = useState(false);
  const [showBlockCallsAlert, setShowBlockCallsAlert] = useState(false);
  const [showRestrictAlert, setShowRestrictAlert] = useState(false);
  const [showReportAlert, setShowReportAlert] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [openThreadMenuId, setOpenThreadMenuId] = useState(null);

  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);

  const [isMeetAndGreetSet, setMeetAndGreetSet] = useState(false);
  const [isMeetAndGreetConfirmed, setMeetAndGreetConfirmed] = useState(false);
  const [isMeetAndGreetCompleted, setMeetAndGreetCompleted] = useState(false);
  const [isAgreementSet, setAgreementSet] = useState(false);
  const [clientFirstName, setClientFirstName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [isStepModalVisible, setStepModalVisible] = useState(false);
  const [clientImageUrl, setClientImageUrl] = useState(defaultAvatar);
  const [workerImageUrl, setWorkerImageUrl] = useState(defaultAvatar);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [mobileView, setMobileView] = useState(
    window.innerWidth <= 768 ? "list" : "desktop"
  );

  const [isSmallMobile, setIsSmallMobile] = useState(
    window.innerWidth <= 480
  );

  const maxTitleLength =
    mobileView !== "desktop" && isSmallMobile ? 45 : 65;

  const [canPlaySound, setCanPlaySound] = useState(false);

  const baseURL = config.API_BASE_URL;

  const navigate = useNavigate();
  const location = useLocation();

  const [scheduleLink, setScheduleLink] = useState("");

  const handleAgreementClick = () => setStepModalVisible(true);

  const selectedThread = activeThread;
  // const noThread = !selectedThread;
  const jobTitle = selectedJob?.jobCraftDetails?.jobTitle ?? "";
  const jobDescription = selectedJob?.jobCraftDetails?.jobDescription;
  const jobLocation = selectedJob?.suburbValue;
  const jobFrequency = selectedJob?.frequencyDetails.supportType;
  const sessions = selectedJob?.frequencyDetails.sessionsPerWeek;
  const hours = selectedJob?.frequencyDetails.totalHoursPerWeek;
  const sessionStartDate = selectedJob?.frequencyDetails.supportStartDate;
  const clientId = selectedThread?.clientId ?? "";
  const clientName = selectedThread?.client ?? "";
  const workerName = selectedThread?.worker ?? "";
  const [isOnline] = useState(true);
  const [showUnreadOnly] = useState(true);
  const [confirmThreadDeleteFor, setConfirmThreadDeleteFor] = useState(null);
  const [deletingThreadId, setDeletingThreadId] = useState(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);

  const userType = useUserRole();

  const messageCount = selectedThread?.fullMessages?.length ?? 0;

  const [isMsgsFocused, setIsMsgsFocused] = useState(false);
  const [bannerMode, setBannerMode] = useState('hidden');

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");

  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [open, setOpen] = useState(false);

  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const ATTACHMENTS_ON_TOP = true;

  const PROFILE_LINK_REGEX =
    /<a\b[^>]*class=["']?view-profile-btn["']?[^>]*>.*?<\/a>/i;

  const { agreement, getAgreementByUserAndJob } = useAgreement();

  const [agreementFlags, setAgreementFlags] = useState({
    workerSigned: false,
    clientSigned: false,
  });

  const userScrollRef = useRef(false);
  const messagesEndRef = useRef(null);
  const newMessagesRef = useRef(null);
  const lastUnreadRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const readMessagesRef = useRef(new Set());
  const dwellTimers = useRef(new Map());
  const observersRef = useRef({});
  const containerRef = messagesContainerRef;
  const listRef = useRef()
  const prevCountRef = useRef(messageCount);
  const prevTotalUnreadRef = useRef(0);
  const prevLastMsgRef = useRef(null);
  const initialLoadRef = useRef(false);
  const processedVisibleRef = useRef(false);
  const meetAndGreetScheduleRef = useRef(null);
  const meetAndGreetJoinRef = useRef(null);
  const confirmOrRescheduleRef = useRef(null);
  const sendAgreementRef = useRef(null);

  const openedWithUnreadRef = useRef(false);
  const autoScrolledRef = useRef(false);
  const bannersScrolledRef = useRef(false);

  const unreadAnchorRef = useRef(-1);
  const initialUnreadCountRef = useRef(0);

  const initialPhaseRef = useRef(false);
  const anchoredMsgIdRef = useRef(null);
  const initialUnreadIdsRef = useRef(new Set());
  const threadOpenTsRef = useRef(0);

  const bannerHideTimerRef = useRef(null);
  const keepBannerInPlaceRef = useRef(false);

  const lastScrollTopRef = useRef(0);
  const scrollDirRef = useRef('down');
  const actionRefs = useRef(new Map());
  const profileAnchorRef = useRef("");
  const filterWrapRef = useRef(null);
  const sortWrapRef = useRef(null);
  const wasHtmlRef = useRef(false);
  const searchAnchorIdRef = useRef(null);


  const now = dateToUnixTimestamp(new Date());

  const unreadIndexes = useMemo(() => {
    return visibleMessages.reduce((acc, m, idx) => {
      if (!m.read && m.receiver === userType) acc.push(idx);
      return acc;
    }, []);
  }, [visibleMessages, userType]);

  const hasUnreadForMe = unreadIndexes.length > 0;

  const unreadCount = unreadIndexes.length;

  const firstUnreadIndex = unreadIndexes[0] ?? -1;
  const lastUnreadIndex = unreadIndexes[unreadIndexes.length - 1] ?? -1;

  const ROW_HEIGHT = 80;
  const OVERSCAN_ROWS = 5;

  const userName = activeThread?.worker ?? 'Unknown';
  const threadJobId = activeThread?.fullMessages?.[0]?.jobId;

  const items = useMemo(() => {
    const out = [];
    visibleMessages.forEach((msg, i, arr) => {
      const ts = Number(msg.chatMessageTimeStamp);
      const thisDate = new Date(ts).toDateString();
      const prevDate = i > 0
        ? new Date(Number(arr[i - 1].chatMessageTimeStamp)).toDateString()
        : null;

      if (i === 0 || thisDate !== prevDate) {
        out.push({ type: 'date', timestamp: ts });
      }
      if (i === firstUnreadIndex) {
        out.push({ type: 'unread-marker', count: unreadCount });
      }
      out.push({ type: 'message', message: msg, idx: i });
    });
    return out;
  }, [visibleMessages, firstUnreadIndex, unreadCount]);


  const firstUnreadMarkerIndex = useMemo(() => {
    return items.findIndex(it => it.type === 'unread-marker');
  }, [items]);

  const isMeetGreetSetup = selectedThread?.meetAndGreetSet;
  const isMeetGreetConfirmed = selectedThread?.meetAndGreetConfirmed;
  const isMeetGreetDone = isMeetGreetSetup && isMeetGreetConfirmed;

  const isClientDone = selectedThread?.hasClientConfirmedCompletionSuccess;
  const isWorkerDone = selectedThread?.hasWorkerConfirmedCompletionSuccess;
  const isMeetAndGreetFullyDone = isMeetGreetDone && isClientDone && isWorkerDone;

  const hasNoUnread = !visibleMessages.some(
    m => !m.read && m.receiver === userRole
  );

  const canJoinMeetAndGreet = isMeetGreetDone
    && !isMeetAndGreetFullyDone
    && hasNoUnread;
  const canSendAgreement =
    isMeetAndGreetFullyDone &&
    agreement === null &&
    hasNoUnread;
  console.log("[Inbox] canSendAgreement:", canSendAgreement, {
    isMeetAndGreetFullyDone,
    agreement,
    hasNoUnread,
  });

  const AUTO_HIDE_BANNER = false;

  const scheduleHideBanner = useCallback((ms = 6000) => {
    if (!AUTO_HIDE_BANNER) return;

    if (bannerHideTimerRef.current) clearTimeout(bannerHideTimerRef.current);
    bannerHideTimerRef.current = setTimeout(() => {
      setBannerMode('hidden');
      initialPhaseRef.current = false;
    }, ms);
  }, []);

  const cancelHideBanner = useCallback(() => {
    if (bannerHideTimerRef.current) {
      clearTimeout(bannerHideTimerRef.current);
      bannerHideTimerRef.current = null;
    }
  }, []);

  const initialUnreadRemaining = useMemo(() => {
    const set = initialUnreadIdsRef.current;
    if (!selectedThread || !set || set.size === 0) return [];
    return (selectedThread.fullMessages || []).filter(
      m => set.has(m.messageId) && !m.read && m.receiver === userType
    );
  }, [selectedThread, userType]);


  const incomingUnread = useMemo(() => {
    const openTs = threadOpenTsRef.current || 0;
    return (selectedThread?.fullMessages || []).filter(
      m =>
        !m.read &&
        m.receiver === userType &&
        !initialUnreadIdsRef.current.has(m.messageId) &&
        Number(m.chatMessageTimeStamp) >= openTs
    );
  }, [selectedThread, userType]);

  const incomingAnchorId = incomingUnread[0]?.messageId ?? null;


  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (!selectedThread) {
      setBannerMode('hidden');
      initialPhaseRef.current = false;
      initialUnreadIdsRef.current = new Set();
      anchoredMsgIdRef.current = null;
      threadOpenTsRef.current = 0;
      return;
    }

    threadOpenTsRef.current = Date.now();

    const fm = selectedThread.fullMessages || [];
    const initialIds = fm
      .filter(m => !m.read && m.receiver === userType)
      .map(m => m.messageId)
      .filter(Boolean);

    initialUnreadIdsRef.current = new Set(initialIds);
    initialPhaseRef.current = initialIds.length > 0;

    const firstUnread = fm.find(m => initialUnreadIdsRef.current.has(m.messageId) && !m.read && m.receiver === userType);
    anchoredMsgIdRef.current = firstUnread?.messageId ?? null;

    setBannerMode(initialIds.length ? 'initial' : 'hidden');
  }, [selectedThread, userType]);

  useEffect(() => {
    console.log(
      "[DEBUG] initialUnreadRemaining:",
      initialUnreadRemaining,
      "length:",
      initialUnreadRemaining.length
    );

    if (initialPhaseRef.current && initialUnreadRemaining.length === 0) {
      console.log("[DEBUG] ✅ All initial unreads cleared → hiding banner.");
      initialPhaseRef.current = false;
    }
  }, [initialUnreadRemaining.length, bannerMode]);

  useEffect(() => {
    if (!selectedThread) return;
    if (initialPhaseRef.current) return;
    const fm = selectedThread.fullMessages || [];
    const last = fm[fm.length - 1] || null;

    if (last && prevLastMsgRef.current?.chatMessageTimeStamp !== last.chatMessageTimeStamp) {
      const isIncomingToMe = last.receiver === userRole && last.sender !== userRole;
      if (isMsgsFocused && isIncomingToMe) {
        const idxs = fm.reduce((acc, m, i) => {
          if (!m.read && m.receiver === userRole) acc.push(i);
          return acc;
        }, []);
        unreadAnchorRef.current = idxs.length ? idxs[0] : -1;
        setBannerMode(idxs.length ? 'incoming' : 'hidden');
      }
      prevLastMsgRef.current = last;
    }
  }, [selectedThread, isMsgsFocused, userRole]);


  useEffect(() => {
    if (!selectedThread) return;
    console.log("selectedThread changed:", selectedThread);
    console.log(
      '🕵️‍♂️ Meet & Greet flags for thread', selectedThread.id,
      'set:', selectedThread.meetAndGreetSet,
      'confirmed:', selectedThread.meetAndGreetConfirmed,
      'completed:', selectedThread.meetAndGreetCompleted,
      'clientYes:', selectedThread.hasClientConfirmedCompletionSuccess,
      'workerYes:', selectedThread.hasWorkerConfirmedCompletionSuccess
    );
  }, [selectedThread]);


  useEffect(() => {
    const handleFirstInteraction = () => {
      setCanPlaySound(true);
      window.removeEventListener('click', handleFirstInteraction);
    };
    window.addEventListener('click', handleFirstInteraction);
    return () => window.removeEventListener('click', handleFirstInteraction);
  }, []);

  useEffect(() => {
    if (!messageId) {
      dispatch({ type: 'SELECT_MESSAGE', payload: null });
      setShowContent(false);
      return;
    }

    if (messages.length === 0) return;

    const threadExists = messages.some(thread => thread.id === messageId);
    if (threadExists) {
      dispatch({ type: 'SELECT_MESSAGE', payload: messageId });
      setShowContent(true);
      if (window.innerWidth <= 768) {
        setMobileView("content");
      }
    } else {
      navigate('/account/ps-dashboard/inbox', { replace: true });
    }
  }, [messageId, messages, dispatch, navigate]);

  useEffect(() => {
    const thread = messages.find((t) => t.id === selectedMessageId);
    setNotificationsEnabled(!!thread?.notificationsEnabled);
  }, [messages, selectedMessageId]);

  useEffect(() => {
    const totalUnread = Object.values(unreadCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    console.log("[PSInbox] unreadCounts object:", unreadCounts);
    console.log(`[PSInbox] totalUnread: ${totalUnread}`);

    if (!initialLoadRef.current) {
      prevTotalUnreadRef.current = totalUnread;
      initialLoadRef.current = true;
      console.log("[PSInbox] 🔧 initialized prevTotalUnread to", totalUnread);
    } else {
      if (totalUnread > prevTotalUnreadRef.current) {
        console.log("[PSInbox] 📢 unread went up → attempting notification sound");

        if (canPlaySound) {
          playNotificationSound().catch(err => {
            console.debug("🔇 play() blocked:", err.message);
          });
        } else {
          console.debug("🔕 sound skipped – user hasn’t clicked yet");
        }
      }

      prevTotalUnreadRef.current = totalUnread;
    }
  }, [unreadCounts, canPlaySound]);

  useEffect(() => {
    const enableUserScroll = () => { userScrollRef.current = true; };
    const disableUserScrollSoon = () => { userScrollRef.current = false; };

    window.addEventListener('wheel', enableUserScroll, { passive: true });
    window.addEventListener('touchmove', enableUserScroll, { passive: true });
    window.addEventListener('keydown', (e) => {
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', 'Space'].includes(e.code)) {
        enableUserScroll();
      }
    });

    return () => {
      window.removeEventListener('wheel', enableUserScroll);
      window.removeEventListener('touchmove', enableUserScroll);
      window.removeEventListener('keydown', enableUserScroll);
    };
  }, []);


  useEffect(() => {
    const root = messagesContainerRef.current;
    if (!showContent || !root || !selectedMessageId) return;

    if (!autoScrolledRef.current && searchAnchorIdRef.current) {
      const anchorId = searchAnchorIdRef.current;
      let attempts = 0;

      const scrollToAnchor = () => {
        const el = root.querySelector(
          `.message-item[data-message-id="${anchorId}"]`
        );
        if (el) {
          const rootRect = root.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();

          const targetTop = rootRect.height * 0.25;
          const delta = (elRect.top - rootRect.top) - targetTop;

          root.scrollTop = Math.max(0, root.scrollTop + delta);
          lastScrollTopRef.current = root.scrollTop;

          autoScrolledRef.current = true;
          searchAnchorIdRef.current = null;
          return;
        }

        attempts += 1;
        if (attempts < 4) {
          requestAnimationFrame(scrollToAnchor);
        } else {
          searchAnchorIdRef.current = null;
        }
      };

      requestAnimationFrame(scrollToAnchor);
      return;
    }

    if (autoScrolledRef.current) return;

    openedWithUnreadRef.current = hasUnreadForMe;

    const HEADER = 136;
    const DATE = 40;
    const BANNER = 35;

    const scrollToBottom = () => {
      root.scrollTop = root.scrollHeight;
      lastScrollTopRef.current = root.scrollTop;
    };

    const placeFirstUnreadBelowDecor = () => {
      const bannerEl = newMessagesRef.current;
      const hasBanner = !!bannerEl;
      const firstUnreadIndicator = root.querySelector(
        ".message-item.incoming .unread-indicator"
      );
      if (!firstUnreadIndicator) return false;

      const firstUnreadEl = firstUnreadIndicator.closest(".message-item");
      if (!firstUnreadEl) return false;

      const TARGET_TOP = HEADER + DATE + (hasBanner ? BANNER : 0);

      const rootRect = root.getBoundingClientRect();
      const unreadRect = firstUnreadEl.getBoundingClientRect();

      const delta = (unreadRect.top - rootRect.top) - TARGET_TOP;
      const nextScrollTop = Math.max(0, root.scrollTop + delta);

      if (Math.abs(root.scrollTop - nextScrollTop) > 1) {
        root.scrollTop = nextScrollTop;
      }

      lastScrollTopRef.current = root.scrollTop;

      const checkRect = firstUnreadEl.getBoundingClientRect();
      const currentTop = checkRect.top - root.getBoundingClientRect().top;
      return Math.abs(currentTop - TARGET_TOP) < 2;
    };

    if (openedWithUnreadRef.current && bannerMode === "initial") {
      let tries = 0;
      const attempt = () => {
        tries += 1;
        const ok = placeFirstUnreadBelowDecor();
        if (!ok && tries < 3) {
          requestAnimationFrame(attempt);
        } else {
          autoScrolledRef.current = true;
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(attempt));
    } else {
      scrollToBottom();
      autoScrolledRef.current = true;
    }
  }, [
    showContent,
    selectedMessageId,
    hasUnreadForMe,
    visibleMessages.length,
    bannerMode,
    isMsgsFocused
  ]);


  useEffect(() => {
    if (!threadJobId) return;
    fetchJobById(threadJobId);
  }, [threadJobId, fetchJobById]);

  useEffect(() => {
    if (!messageId || !messages.some(thread => thread.id === messageId)) {
      dispatch({ type: 'SELECT_MESSAGE', payload: null });
      setShowContent(false);
      navigate('/account/ps-dashboard/inbox', { replace: true });
    }
  }, [messageId, messages, dispatch, navigate]);



  useEffect(() => {
    if (!clientId || !userId) return;
    const { clientImageUrl, workerImageUrl } = fetchAvatars({
      clientId,
      workerId: userId,
      defaultAvatar,
      imgUrl
    });
    setClientImageUrl(clientImageUrl);
    setWorkerImageUrl(workerImageUrl);
  }, [selectedMessageId, clientId, userId]);

  useEffect(() => {
    if (shouldScrollToBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom]);

  useEffect(() => {
    function onResize() {
      setMobileView(window.innerWidth <= 768 ? "list" : "desktop");
      setIsSmallMobile(window.innerWidth <= 480);
    }
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!showContent) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const seen = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.dataset.messageId;
        if (!id) return;

        const visibleEnough = entry.intersectionRatio >= 0.75;

        if (visibleEnough && userScrollRef.current && scrollDirRef.current === 'down') {
          if (!dwellTimers.current.has(id)) {
            const to = setTimeout(() => {
              markReadOptimistic(selectedMessageId, [id]);
              dwellTimers.current.delete(id);
            }, 300);
            dwellTimers.current.set(id, to);
          }
        } else {
          const to = dwellTimers.current.get(id);
          if (to) {
            clearTimeout(to);
            dwellTimers.current.delete(id);
          }
        }
      });
    }, { root: messagesContainerRef.current, threshold: [0, 0.25, 0.5, 0.75, 1] });

    const observeNew = () => {
      container.querySelectorAll(".message-item.incoming").forEach((el) => {
        const id = el.dataset.messageId;
        const isUnread = !!el.querySelector(".unread-indicator");
        if (id && isUnread && !seen.has(id)) {
          seen.add(id);
          observer.observe(el);
        }
      });
    };

    observeNew();
    container.addEventListener("scroll", observeNew, { passive: true });

    return () => {
      container.removeEventListener("scroll", observeNew);
      observer.disconnect();
    };
  }, [showContent, selectedMessageId, visibleMessages.length]);


  useEffect(() => {
    if (!lastUnreadRef.current || !messagesContainerRef.current) return;
    const container = messagesContainerRef.current;

    const lastObs = new IntersectionObserver((entries) => {
      const e = entries[0];
      const fullyVisible = e.isIntersecting && e.intersectionRatio === 1;

      if (fullyVisible && userScrollRef.current) {
        const ids = Array.from(readMessagesRef.current);
        if (ids.length > 0 && selectedMessageId) {
          dispatch({
            type: 'MARK_MESSAGES_READ_BULK',
            payload: { threadId: selectedMessageId, messageIds: ids },
          });

          axios
            .patch(`${baseURL}/v1/messages/markReadBulk/${selectedMessageId}`, ids)
            .then(() => fetchUnreadCount())
            .catch(err => console.error("Persisting reads failed:", err));

          readMessagesRef.current.clear();

        }
      }
    }, { root: container, threshold: 1.0 });

    lastObs.observe(lastUnreadRef.current);
    return () => lastObs.disconnect();
  }, [selectedMessageId, baseURL, dispatch]);

  useEffect(() => {
    if (!showContent || !messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const marker = newMessagesRef.current;

    keepBannerInPlaceRef.current = false;

    if (initialPhaseRef.current && marker) {
      const overflow = container.scrollHeight > container.clientHeight;
      const markerTop = marker.offsetTop - container.offsetTop;
      const markerBelowFold = markerTop > container.clientHeight;

      keepBannerInPlaceRef.current = overflow && markerBelowFold;

      if (keepBannerInPlaceRef.current) {
        cancelHideBanner();
        scheduleHideBanner(6000);
      }
    }
  }, [
    showContent,
    selectedMessageId,
    visibleMessages.length,
    cancelHideBanner,
    scheduleHideBanner
  ]);


  useEffect(() => {
    return () => {
      flushPendingReads();
    };
  }, [flushPendingReads, selectedMessageId]);

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') flushPendingReads(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('beforeunload', flushPendingReads);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeunload', flushPendingReads);
    };
  }, [flushPendingReads]);

  useEffect(() => {
    const shouldShowJoin =
      selectedThread?.meetAndGreetSet &&
      selectedThread?.meetAndGreetConfirmed;

    const hasUnread = visibleMessages.some(
      (m) => !m.read && m.receiver === userType
    );

    if (
      shouldShowJoin &&
      !hasUnread &&
      !openedWithUnreadRef.current &&
      !userScrollRef.current
    ) {
      meetAndGreetJoinRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [
    selectedThread?.meetAndGreetSet,
    selectedThread?.meetAndGreetConfirmed,
    visibleMessages,
    userType
  ]);

  useEffect(() => {
    const userConfirmed =
      selectedThread?.hasClientConfirmedCompletionSuccess ||
      selectedThread?.hasWorkerConfirmedCompletionSuccess;

    const hasUnread = visibleMessages.some(
      (m) => !m.read && m.receiver === userRole
    );

    if (
      selectedThread?.meetAndGreetSet &&
      selectedThread?.meetAndGreetConfirmed &&
      userConfirmed &&
      !hasUnread &&
      !openedWithUnreadRef.current &&
      !userScrollRef.current
    ) {
      sendAgreementRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [
    selectedThread?.meetAndGreetSet,
    selectedThread?.meetAndGreetConfirmed,
    selectedThread?.hasClientConfirmedCompletionSuccess,
    selectedThread?.hasWorkerConfirmedCompletionSuccess,
    userRole,
    visibleMessages,
  ]);

  useEffect(() => {
    console.log(`[PSInbox] ▶️ effect for selectedMessageId:`, selectedMessageId);

    autoScrolledRef.current = true;
    bannersScrolledRef.current = false;

    return () => {
      const ids = Array.from(readMessagesRef.current);

      console.log(`[PSInbox] 🔄 cleanup for selectedMessageId:`, selectedMessageId);
      console.log('— flushing IDs:', ids);

      if (ids.length > 0) {
        axios
          .patch(`${baseURL}/v1/messages/markReadBulk/${selectedMessageId}`, ids)
          .catch(err => console.error("Persisting reads failed:", err));
        readMessagesRef.current.clear();
      }
    };
  }, [selectedMessageId]);

  const remainingUnread = useMemo(
    () => (visibleMessages || []).filter(m => !m.read && m.receiver === userType).length,
    [visibleMessages, userType]
  );

  useEffect(() => {
    if (remainingUnread === 0) {
      unreadAnchorRef.current = -1;
      scheduleHideBanner(6000);
    } else {
      cancelHideBanner();
    }
  }, [remainingUnread, scheduleHideBanner, cancelHideBanner]);

  useEffect(() => {
    return () => {
      if (bannerHideTimerRef.current) clearTimeout(bannerHideTimerRef.current);
    };
  }, []);

  const handleGlobalClickCapture = (e) => {
    const target = e.target;

    if (openThreadMenuId) {
      const threadMenuRoot = target.closest(".thread-actions-control");
      if (!threadMenuRoot) {
        setOpenThreadMenuId(null);
      }
    }

    if (showActionsFor) {
      const msgActionsRoot = target.closest(".msg-actions");
      if (!msgActionsRoot) {
        setShowActionsFor(null);
      }
    }

    if (isFilterOpen && filterWrapRef.current) {
      if (!filterWrapRef.current.contains(target)) {
        setIsFilterOpen(false);
      }
    }

    if (isSortOpen && sortWrapRef.current) {
      if (!sortWrapRef.current.contains(target)) {
        setIsSortOpen(false);
      }
    }
  };


  const scrollToBanner = (ref) => {
    if (!showContent) return;
    if (!selectedMessageId) return;
    if (hasUnreadForMe) return;
    if (bannersScrolledRef.current) return;

    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      bannersScrolledRef.current = true;
    }
  };

  const handleToggleNotifications = useCallback(async () => {
    if (!selectedMessageId || !userId) {
      console.warn("[ChatInfoPane] Missing selectedMessageId or userId");
      return;
    }

    try {
      const updated = await toggleThreadNotifications(selectedMessageId, userId);
      setNotificationsEnabled(!!updated.notificationsEnabled);
    } catch (err) {
      console.error("[ChatInfoPane] toggleNotifications failed", err);
    }
  }, [toggleThreadNotifications, selectedMessageId, userId]);

  const adjustMessageType = (message, userType) =>
    message.sender === userType
      ? "outgoing"
      : "incoming";

  const isImage = (file) => /image\//i.test(file.type || "");

  const openFilePicker = () => fileInputRef.current?.click();

  const onFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    const MAX_FILE = 10 * 1024 * 1024;
    const MAX_TOTAL = 50 * 1024 * 1024;
    const ok = picked.filter(f => f.size <= MAX_FILE);
    const total = ok.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL) {
      alert("Total attachments exceed 50MB.");
      return;
    }

    const classify = (f) => {
      if (f.type.startsWith("image/")) return "image";
      if (f.type.startsWith("video/")) return "video";
      if (f.type === "application/pdf") return "pdf";
      return "file";
    };

    const withPreviews = ok.map(f => ({
      file: f,
      kind: classify(f),
      previewUrl: (f.type.startsWith("image/") || f.type.startsWith("video/"))
        ? URL.createObjectURL(f)
        : null
    }));

    setFiles(prev => [...prev, ...withPreviews]);
    e.target.value = "";
  };

  const attUrl = (att) => {
    if (att?.url) return att.url;
    const name = att?.objectKey || att?.fileName || att?.name;
    return name ? `${config.ATTACHMENTS_BASE_URL}/${name}` : "";
  };

  const uploadFiles = async (threadId, fileList) => {
    if (!fileList?.length) return [];

    const form = new FormData();
    form.append("userId", userId);
    fileList.forEach(({ file }) => form.append("files", file));

    try {
      const { data } = await axios.post(
        `${baseURL}/v1/chat-attachments/messages/${threadId}/upload`,
        form
      );
      return (Array.isArray(data) ? data : []).map(d => ({
        id: d.id,
        fileName: d.fileName,
        mimeType: d.mimeType,
        size: d.size,
        url: d.url || `${config.ATTACHMENTS_BASE_URL}/${d.objectKey || d.fileName}`,
      }));
    } catch (e) {
      console.error("Upload failed:", e);
      return [];
    }
  };

  const removePendingFile = (idx) => {
    setFiles(prev => {
      const f = prev[idx];
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearPreviewsAfterSend = () => {
    files.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
  };

  const handleThreadClick = (thread, e) => {
    console.log("Thread clicked");

    if (
      e?.target?.closest(".thread-actions-control") ||
      e?.target?.closest(".thread-actions-menu")
    ) {
      return;
    }

    const term = searchTerm.trim().toLowerCase();

    if (term) {
      const hitInfo = searchHits.get(thread.id);
      const anchorMsg = hitInfo?.primary;

      searchAnchorIdRef.current = anchorMsg?.messageId || null;
    } else {
      searchAnchorIdRef.current = null;
    }

    dispatch({ type: "SELECT_MESSAGE", payload: thread.id });
    setShowContent(true);
    if (mobileView !== "desktop") setMobileView("content");
    navigate(`/account/ps-dashboard/inbox/${thread.id}`);
    setShowScheduler(false);
  };

  const getNextStep = () => {
    if (!isMeetAndGreetSet) {
      return "Please set up a meet and greet session.";
    } else if (isMeetAndGreetSet && !isMeetAndGreetConfirmed) {
      return "Waiting for confirmation of the meet and greet session.";
    } else if (
      isMeetAndGreetSet &&
      isMeetAndGreetConfirmed &&
      !isMeetAndGreetCompleted
    ) {
      return "Meet and greet confirmed. Awaiting completion of the session.";
    } else if (
      isMeetAndGreetSet &&
      isMeetAndGreetConfirmed &&
      isMeetAndGreetCompleted &&
      !isAgreementSet
    ) {
      return "Meet and greet completed. It's time to set up the agreement.";
    }
    return "All steps completed.";
  }

  const handleBackClick = () => {
    setShowContent(false);
    if (mobileView !== "desktop") setMobileView("list");
  };

  const handleInfoClick = () => {
    if (mobileView !== "desktop") {
      setMobileView("info");
      return;
    }
    setChatInfoPane(!chatInfoPane);
  };

  const handleCloseClick = () => {
    if (mobileView !== "desktop") {
      setMobileView("content");
      return;
    }
    setChatInfoPane(false);
    setShowContent(true);
  };

  const queueMark = (id) => {
    if (!id || dwellTimers.current.has(id)) return;
    const to = setTimeout(() => {
      markReadOptimistic(selectedMessageId, [id]);
      dwellTimers.current.delete(id);
    }, 300);
    dwellTimers.current.set(id, to);
  };

  const sweepVisibleUnread = () => {
    const root = messagesContainerRef.current;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();

    root.querySelectorAll(".message-item.incoming .unread-indicator").forEach(ind => {
      const el = ind.closest(".message-item.incoming");
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const visibleHeight = Math.min(rect.bottom, rootRect.bottom) - Math.max(rect.top, rootRect.top);
      const clamped = Math.max(0, Math.min(visibleHeight, rect.height));
      const ratio = rect.height > 0 ? clamped / rect.height : 0;

      if (ratio >= 0.75) {
        const id = el.dataset.messageId;
        queueMark(id);
      }
    });
  };

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    scrollDirRef.current = scrollTop > lastScrollTopRef.current ? 'down' : 'up';
    lastScrollTopRef.current = scrollTop;

    if (userScrollRef.current && scrollDirRef.current === 'down') {
      sweepVisibleUnread();
    }
  };

  const psSendMessage = async () => {
    if (!text.trim() || !selectedMessageId) return;

    const uploaded = await uploadFiles(selectedMessageId, files);
    const attachments = uploaded.length ? uploaded : undefined;

    const newMsg = {
      text,
      chatMessageTimeStamp: now,
      sender: userType,
      receiver: userType === "worker" ? "client" : "worker",
      jobId,
      read: false,
      ...(attachments ? { attachments } : {}),
    };

    const updatedThreads = state.messages.map(thread => {
      if (thread.id !== selectedMessageId) return thread;
      return {
        ...thread,
        fullMessages: [...thread.fullMessages, newMsg],
      };
    });

    dispatch({ type: "SET_MESSAGES", payload: updatedThreads });

    setText("");
    clearPreviewsAfterSend();
    setShouldScrollToBottom(true);

    try {
      await axios.post(
        `${baseURL}/v1/messages/addChatMessage/${selectedMessageId}`,
        newMsg
      );
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const isClientReplyToWorker = (fullMessages) => {
    if (fullMessages.length >= 2) {
      return (
        fullMessages[0].sender === "worker" &&
        fullMessages[1].sender === "client"
      );
    }
    return false;
  };

  const handleReopen = (state) => {
    setShowBlockCallsAlert(state);
  };

  const toggleAlert = () => {
    setShowBlockCallsAlert(true);
  };

  const renderMessageStatus = (message) => {
    const isMine = message.sender === userType;
    const isRead = !!message.read;
    const wasEdited = !!message.editedAt;

    const editedTitle = wasEdited
      ? `Edited ${formatUnixFullDate(message.editedAt)} • ${formatUnixFullTime(message.editedAt)}`
      : "";

    return (
      <span className="message-status-wrap">
        {wasEdited && (
          <span className="edited-badge" title={editedTitle} aria-label="Edited">
            Edited
          </span>
        )}

        {isMine && (
          <span
            className={`message-status-dot ${isRead ? "read" : "delivered"}`}
            title={isRead ? "Read" : "Sent"}
            aria-label={isRead ? "Read" : "Sent"}
          >
            <span className="status-icon">
              <PiChecksBold />
            </span>
            <span className="message-status-text">{isRead ? "Read" : "Sent"}</span>
          </span>
        )}
      </span>
    );
  };


  const handleFilterChange = (selected) => {
    const newVal = selected?.[0]?.value || "all";
    setFilterBy(newVal);
  };

  const cleanLatestTextMessage = (htmlString) => {
    htmlString = htmlString.replace(/<(script|style).*?>.*?<\/\1>/gi, "");
    const textArea = document.createElement("textarea");
    textArea.innerHTML = htmlString;
    htmlString = textArea.value;
    htmlString = htmlString.replace(/<[^>]*>/g, "");
    htmlString = htmlString.replace(/\s+/g, " ");
    htmlString = htmlString.trim();
    return htmlString.substring(0, 18);
  };

  const sanitizeAndParseHtml = (htmlString) => {
    const cleanHtml = DOMPurify.sanitize(htmlString || "");

    const term = searchTerm?.trim();
    const termLower = term ? term.toLowerCase() : null;

    const highlightText = (text) => {
      if (!termLower) return text;

      const lower = text.toLowerCase();
      if (!lower.includes(termLower)) {
        return text;
      }

      const parts = [];
      let lastIndex = 0;
      let idx;

      while ((idx = lower.indexOf(termLower, lastIndex)) !== -1) {
        if (idx > lastIndex) {
          parts.push(text.slice(lastIndex, idx));
        }

        const matchText = text.slice(idx, idx + term.length);

        parts.push(
          <mark
            className="search-highlight"
            key={`${idx}-${matchText}`}
          >
            {matchText}
          </mark>
        );

        lastIndex = idx + term.length;
      }

      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }

      return parts.length ? parts : text;
    };

    const options = {
      replace: (node) => {
        const { type, name, attribs, children, data } = node;

        if (type === "text" && typeof data === "string") {
          const highlighted = highlightText(data);

          if (highlighted === data) {
            return data;
          }

          return <>{highlighted}</>;
        }

        if (!name) return;

        if (name === "button" && attribs?.class === "view-profile-btn") {
          return (
            <button
              className="view-profile-btn"
              onClick={handleConfirmAttendance}
            >
              {domToReact(children, options)}
            </button>
          );
        }

        if (name === "p") {
          return (
            <p className="message-text">
              {domToReact(children, options)}
            </p>
          );
        }

        if (name === "a") {
          const { class: _class, ...rest } = attribs;
          return (
            <a
              className={_class || "message-text"}
              {...rest}
            >
              {domToReact(children, options)}
            </a>
          );
        }

        return undefined;
      },
    };

    return parse(cleanHtml, options);
  };

  const stripHtmlToText = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").trim();
  };

  const buildHighlightedSnippet = (html, term) => {
    const plain = stripHtmlToText(html);
    if (!term) return plain;

    const t = term.trim();
    if (!t) return plain;

    const lower = plain.toLowerCase();
    const lowerTerm = t.toLowerCase();
    const idx = lower.indexOf(lowerTerm);

    if (idx === -1) return plain;

    const SNIPPET_RADIUS = 24;
    const start = Math.max(0, idx - SNIPPET_RADIUS);
    const end = Math.min(plain.length, idx + t.length + SNIPPET_RADIUS);

    const prefix = (start > 0 ? "…" : "") + plain.slice(start, idx);
    const match = plain.slice(idx, idx + t.length);
    const suffix = plain.slice(idx + t.length, end) + (end < plain.length ? "…" : "");

    return (
      <>
        {prefix}
        <mark className="search-highlight">{match}</mark>
        {suffix}
      </>
    );
  };

  const handleJobTitleClick = () => {
    if (!jobId) return;
    window.open(
      `/account/ps-dashboard/job-listings/open/${jobId}`,
      "_blank"
    );
  };

  const handleScheduleSet = ({ date, time, duration, preferredMethod }) => {
    const formattedDate = `${String(date.month).padStart(2, '0')}/${String(date.day).padStart(2, '0')}/${date.year}`;
    const formattedTime = `${time.hours}:${String(time.minutes).padStart(2, '0')} ${time.ampm}`;
    console.log("Received data from onScheduleSet:", {
      date,
      time,
      duration,
      preferredMethod,
    });

    const confirmButton = `
  <button class="view-profile-btn">
    Confirm Attendance
  </button>
`;

    const messageText = `
  <p>Introductory Meet and Greet session scheduled.</p>
  <p>&nbsp;</p>
  <p>Date : <strong> ${formattedDate}</strong></p>
  <p>Time : <strong> ${formattedTime}</strong></p>
  <p>Duration : <strong> ${duration}</strong></p>
  <p>Preferred method : <strong>${preferredMethod}</strong></p>
  <p>&nbsp;</p>
  <p>${confirmButton}</p>
`;

    sendOutgoingMessage(messageText);
  };

  const sendOutgoingMessage = async (text) => {
    if (!text.trim() || !selectedMessageId) return;

    const uploaded = await uploadFiles(selectedMessageId, files);
    const attachments = uploaded.length ? uploaded : undefined;

    const newMsg = {
      text,
      chatMessageTimeStamp: now,
      sender: userType, receiver: userType === "worker"
        ? "client"
        : "worker",
      jobId,
      read: false,
      ...(attachments ? { attachments } : {})
    };

    console.log("sendoutgoing payload:", newMsg);

    dispatch({
      type: MESSAGE_SENT,
      payload: {
        threadId: selectedMessageId,
        message: newMsg
      }
    });

    setText("");
    setFiles([]);
    setShouldScrollToBottom(true);

    try {
      const res = await axios.post(
        `${baseURL}/v1/messages/addChatMessage/${selectedMessageId}`,
        newMsg
      );
      console.log("psSendMessage server response:", res.data);
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const extractProfileLink = function (html) {
    const match = html.match(PROFILE_LINK_REGEX);
    return {
      stripped: html.replace(PROFILE_LINK_REGEX, ""),
      anchor: match ? match[0] : ""
    };
  };

  const startEdit = (msg) => {
    setEditingId(msg.messageId);

    wasHtmlRef.current = /<[^>]+>/.test(msg.text || "");

    let src = (msg.text || "").replace(
      /<p>\s*<button\b[^>]*>\s*Confirm Attendance\s*<\/button>\s*<\/p>/gi,
      ""
    );

    const { stripped, anchor } = extractProfileLink(src);
    profileAnchorRef.current = anchor;

    setEditDraft(stripped);
    setShowActionsFor(null);
  };


  function stripTagsToPlain(html) {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("br").forEach(br => br.replaceWith("\n"));
    return div.textContent || "";
  }

  function plainToParagraphHtml(plain) {
    const lines = plain.split(/\r?\n/).map(l => l.trimEnd());
    while (lines.length && !lines[lines.length - 1]) lines.pop();
    if (!lines.length) return "<p></p>";
    return `<p>${lines.map(l => l || "").join("<br>")}</p>`;
  }

  function normalizeSavedHtml(html) {
    const wrap = document.createElement("div");
    wrap.innerHTML = html;

    while (
      wrap.lastChild &&
      (
        wrap.lastChild.nodeName === "BR" ||
        (wrap.lastChild.nodeName === "P" && !wrap.lastChild.textContent.trim()) ||
        (wrap.lastChild.nodeType === 3 && !wrap.lastChild.nodeValue.trim())
      )
    ) {
      wrap.removeChild(wrap.lastChild);
    }

    return wrap.innerHTML || "<p></p>";
  }

  const saveEdit = async (msg) => {
    let cleaned;

    if (wasHtmlRef.current) {
      cleaned = normalizeSavedHtml(editDraft || "");
    } else {
      const plain = stripTagsToPlain(editDraft || "");
      cleaned = plainToParagraphHtml(plain);
      cleaned = normalizeSavedHtml(cleaned);
    }

    if (profileAnchorRef.current) {
      const safeAnchor = DOMPurify.sanitize(profileAnchorRef.current, {
        ALLOWED_TAGS: ["a"],
        ALLOWED_ATTR: ["href", "target", "rel", "class"]
      });

      const container = document.createElement("div");
      container.innerHTML = cleaned;

      const last = container.lastElementChild;
      if (!last || (last.tagName === "P" && last.innerHTML.trim() !== "")) {
        const spacer = document.createElement("p");
        spacer.innerHTML = "&nbsp;";
        container.appendChild(spacer);
      }

      const linkP = document.createElement("p");
      linkP.innerHTML = safeAnchor;
      container.appendChild(linkP);

      cleaned = normalizeSavedHtml(container.innerHTML);
    }

    const optimisticUpdatedThreads = state.messages.map(t => {
      if (t.id !== selectedMessageId) return t;
      const full = (t.fullMessages || []).map(m =>
        m.messageId === msg.messageId
          ? { ...m, text: cleaned, editedAt: Date.now() }
          : m
      );
      const isLast =
        t.fullMessages?.[t.fullMessages.length - 1]?.messageId === msg.messageId;
      return {
        ...t,
        fullMessages: full,
        latestMessage: isLast ? cleaned : t.latestMessage
      };
    });
    dispatch({ type: "SET_MESSAGES", payload: optimisticUpdatedThreads });

    try {
      const serverThread = await editMessage(selectedMessageId, msg.messageId, {
        text: cleaned,
        editReason: "user_edit",
        userId,
        userRole
      });
      if (serverThread?.id) {
        const reconciled = state.messages.map(t =>
          t.id === serverThread.id ? serverThread : t
        );
        dispatch({ type: "SET_MESSAGES", payload: reconciled });
      }
    } catch (e) {
      console.error("❌ [saveEdit] Failed to save edit:", e);
    } finally {
      cancelEdit();
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const handleConfirmAttendance = useCallback(async () => {
    console.log("handleConfirmAttendance Invoked");
    if (!selectedMessageId) return;

    const details = selectedThread?.meetAndGreetDetails || {};

    const sessionName = `session_${Math.random().toString(16).slice(2, 10)}`;

    const payload = {
      isMeetAndGreetConfirmed: true,
      meetingConfirmedBy: userId,
      meetAndGreetSessionName: sessionName,
      meetAndGreetDetails: {
        meetAndGreetDate: details.meetAndGreetDate,
        meetAndGreetTime: details.meetAndGreetTime,
        preferredMethod: details.preferredMethod,
        duration: details.duration,
        isVideoCallCompleted: false,
        isPhoneCallCompleted: false,
        isInPersonCompleted: false,
      },
      newMessage: {
        chatMessageTimeStamp: now,
        text: `✅ Meet & Greet confirmed for ${details.date} at ${details.time}.`,
        sender: userRole,
        receiver: userRole === "worker" ? "client" : "worker",
        jobId,
        read: false,
        chatMessageReadTimeStamp: 0
      },
    };

    console.log("payload to patch : ", payload);

    try {
      const res = await axios.patch(
        `${baseURL}/v1/messages/confirm-invite/${selectedMessageId}`,
        payload
      );
      console.log("✅ Meet & Greet confirmed:", res.data);
    } catch (err) {
      console.error("❌ Error confirming Meet & Greet:", err);
    }
  }, [
    selectedMessageId,
    selectedThread,
    userId,
    userRole,
    jobId
  ]);

  const handleSessionTerminationConfirmation = useCallback(
    async (successful) => {
      if (!selectedMessageId) {
        console.error('Thread ID is missing. Cannot send feedback.');
        return;
      }
      const threadId = selectedMessageId;
      const timestamp = Date.now();
      const payload = { userRole, successful, timestamp };
      console.log('Payload:', payload);
      try {
        await axios.patch(
          `${baseURL}/v1/messages/meeting-completion-status/${threadId}`,
          payload
        );
        console.log('Update response:', payload);
      } catch (error) {
        console.error('Failed to send feedback:', error);
        return;
      }
      const now = Date.now();
      const thread = state.messages.find(t => t.id === threadId);
      const jobId = thread?.fullMessages?.[0]?.jobId;
      const text = successful
        ? `✅ <strong>${userName}</strong> <strong>(${userRole})</strong> confirmed the Meet &amp; Greet completed successfully.`
        : `❌ <strong>${userName}</strong> <strong>(${userRole})</strong> indicated the Meet &amp; Greet needs to be rescheduled.`;
      const newMsg = {
        text,
        chatMessageTimeStamp: now,
        sender: userRole,
        receiver: userRole === 'worker' ? 'client' : 'worker',
        jobId,
        read: false,
      };
      dispatch({ type: MESSAGE_SENT, payload: { threadId, message: newMsg } });
      try {
        await axios.post(
          `${baseURL}/v1/messages/addChatMessage/${threadId}`,
          newMsg
        );
      } catch (err) {
        console.error('Error sending chat message:', err);
      }
    },
    [baseURL, dispatch, state.messages, selectedMessageId, userRole, selectedThread]
  );

  const navigateToAgreementSetup = () => {
    const cleanedJobDescription = cleanJobDescription(jobDescription);

    const {
      supportType: jobFrequency,
      sessionsPerWeek: sessions,
      totalHoursPerWeek: hoursPerWeek,
      supportStartDate: selectedJobStartDate,
      preferredDays,
      timeSlots,
    } = selectedJob.frequencyDetails || {};

    const rawGreetingRate = serviceRates["Initial greeting session"] || "";
    const noFeeForInitialGreetingSession = parseFloat(rawGreetingRate) <= 0;
    const initialGreetingSessionRate = rawGreetingRate;

    const includeTravelRate = false;
    const travelRatePerMile = "";

    const initialEditorContent = "";

    const agreementSetupState = {
      messageId: selectedMessageId,
      clientId,
      clientName,
      workerId: userId,
      createdBy: userRole,
      jobId,
      jobFrequency,
      selectedJobStartDate,
      workerName,
      jobLocation,
      jobTitle,
      jobDescription: cleanedJobDescription,
      sessions,
      hoursPerWeek,
      totalAgreementHours: sessions * hoursPerWeek,
      rates: initialRates,
      offeredRateTypes,
      preferredDays,
      timeSlots,
      noFeeForInitialGreetingSession,
      initialGreetingSessionRate,
      includeTravelRate,
      travelRatePerMile,
      editorContent: initialEditorContent || "",
      agreementDate: 0,
      viewedAt: 0,
      workerSignedAt: 0,
      clientSignedAt: 0,
      workerSigned: false,
      clientSigned: false,
      status: "PARTIALLY_SIGNED_PENDING",
      executionStage: "PENDING",
    };

    console.log("→ navigating with:", agreementSetupState);

    navigate("/account/ps-dashboard/agreement-setup", {
      state: agreementSetupState,
    });
  };


  const handleCloseScheduler = () => {
    setShowScheduler(false);
  };

  const handleJoinMeetAndGreet = () => {
    const preferredMethod = selectedThread.meetAndGreetDetails.meetAndGreetPreferredMethod;
    const sessionName = selectedThread.meetAndGreetSessionName;
    const threadId = selectedThread.id;

    console.log("handleJoinMeetAndGreet → threadId:   ", threadId);
    console.log("handleJoinMeetAndGreet → preferredMethod:", preferredMethod);
    console.log("handleJoinMeetAndGreet → sessionName:   ", sessionName);

    const userId = selectedThread.clientId;

    const userName = selectedThread.client;

    if (!sessionName) {
      console.error("No session name available for navigation.");
      return;
    }
    navigate(
      `/account/ps-dashboard/meeting/${preferredMethod}/${sessionName}/${threadId}`,
      { replace: false }
    );
  };

  const handleReschedule = () => {
    setShowScheduler(true);
  };

  const togglePin = async (msg) => {
    const pinned = !Boolean(msg.pinned);

    const optimistic = state.messages.map(t => {
      if (t.id !== selectedMessageId) return t;
      const full = (t.fullMessages || []).map(m =>
        m.messageId === msg.messageId ? { ...m, pinned } : m
      );
      return { ...t, fullMessages: full };
    });
    dispatch({ type: "SET_MESSAGES", payload: optimistic });

    try {
      await axios.patch(
        `${baseURL}/v1/messages/${selectedMessageId}/${msg.messageId}/pin`,
        { pinned }
      );
    } catch (e) {
      const revert = state.messages.map(t => {
        if (t.id !== selectedMessageId) return t;
        const full = (t.fullMessages || []).map(m =>
          m.messageId === msg.messageId ? { ...m, pinned: !pinned } : m
        );
        return { ...t, fullMessages: full };
      });
      dispatch({ type: "SET_MESSAGES", payload: revert });
      console.error("Toggle pin failed:", e);
    } finally {
      setShowActionsFor(null);
    }
  };

  const toggleThreadMenu = (e, id) => {
    e.stopPropagation();
    setOpenThreadMenuId(prev => (prev === id ? null : id));
  };

  const closeThreadMenu = () => setOpenThreadMenuId(null);

  const onTogglePinThread = async (e, thread) => {
    e.stopPropagation();
    try {
      console.log("[PSInbox] Toggling pin for thread:", thread.id, "userId:", userId);
      const updated = await pinThread(thread.id, !thread.pinned, userId);
      console.log("Pin updated:", updated.pinned);
    } catch (err) {
      console.error("[PSInbox] onTogglePinThread error:", err);
    }
  };

  const onToggleArchiveThread = async (e, thread) => {
    e.stopPropagation();
    try {
      const nextArchived = !Boolean(thread.chatArchived);
      console.log("[PSInbox] Toggling archive for thread:", thread.id, "userId:", userId);
      const updated = await archiveThread(thread.id, nextArchived, userId);
      console.log("Archive updated:", updated.chatArchived);
    } catch (err) {
      console.error("[PSInbox] onToggleArchiveThread error:", err);
    } finally {
      closeThreadMenu();
    }
  };

  const onSoftDeleteThread = async (e, thread) => {
    e.stopPropagation();
    if (!thread?.id) {
      console.warn("[PSInbox] Missing thread or id in onSoftDeleteThread");
      return;
    }
    try {
      console.log("[PSInbox] Soft-deleting thread:", thread.id, "userId:", userId);
      const result = await softDeleteThread(thread.id, userId);
      console.log("Soft delete result:", result);
    } catch (err) {
      console.error("[PSInbox] onSoftDeleteThread error:", err);
    } finally {
      closeThreadMenu();
    }
  };


  const onTogglePinMessage = async (msg) => {
    if (!msg?.messageId || !selectedMessageId) {
      console.warn("[PSInbox] Missing messageId or threadId in onTogglePinMessage");
      return;
    }

    try {
      const newPinnedState = !Boolean(msg.pinned);
      console.log(
        "[PSInbox] Toggling pin for message:",
        msg.messageId,
        "thread:",
        selectedMessageId,
        "→",
        newPinnedState
      );

      const result = await pinMessage(selectedMessageId, msg.messageId, newPinnedState, userId);
      console.log("✅ Message pin updated:", result);
    } catch (err) {
      console.error("[PSInbox] onTogglePinMessage error:", err);
    }
  };


  const onSoftDeleteMessage = async (msg) => {
    if (!msg?.messageId || !selectedMessageId) {
      console.warn("[PSInbox] Missing messageId or threadId in onSoftDeleteMessage");
      return;
    }

    try {
      console.log(
        "[PSInbox] Soft-deleting message:",
        msg.messageId,
        "thread:",
        selectedMessageId,
        "userId:",
        userId
      );

      const result = await softDeleteMessage(selectedMessageId, msg.messageId, userId);
      console.log("✅ Message deleted successfully:", result);
    } catch (err) {
      console.error("[PSInbox] onSoftDeleteMessage error:", err);
    }
  };

  return (
    <div className="inbox-container"
      onClickCapture={handleGlobalClickCapture}
    >
      <div className="message-list-scroll-wrapper"
        style={{
          display:
            mobileView === "desktop" || mobileView === "list"
              ? undefined
              : "none",
        }}>
        <div className="message-list">
          <div className="message-list-header">
            <div className="header-controls">
              <div className="search-wrapper">
                <FaSearch className="message-search-icon" />

                <input
                  type="search"
                  placeholder="Search chats or clients…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {searchTerm.trim() !== "" && (
                  <span
                    className="clear-search-btn"
                    onClick={() => setSearchTerm("")}
                    role="button"
                    aria-label="Clear search"
                  >
                    ×
                  </span>
                )}
              </div>

              <div className="sort-mark-container">

                <div className="sort-control" ref={filterWrapRef}>
                  <button
                    type="button"
                    className={`sort-trigger ${isFilterOpen ? "open" : ""}`}
                    onClick={() => setIsFilterOpen(v => !v)}
                    aria-haspopup="menu"
                    aria-expanded={isFilterOpen}
                    aria-controls="filter-menu"
                  >
                    <TbFilterCog className="sort-icon" />
                    <span className="sort-label">Filter</span>
                    <FaChevronDown className={`chev ${isFilterOpen ? "rot" : ""}`} />
                  </button>

                  {isFilterOpen && (
                    <div className="sort-menu" id="filter-menu" role="menu">
                      {filterOptions.map((opt) => (
                        <button
                          key={opt.value}
                          role="menuitemradio"
                          aria-checked={filterBy === opt.value}
                          className={`sort-item ${filterBy === opt.value ? "active" : ""}`}
                          onClick={() => {
                            setFilterBy(opt.value);
                            setIsFilterOpen(false);
                          }}
                        >
                          <span className="item-text">{opt.label}</span>
                          {filterBy === opt.value && <FaCheck className="check" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="sort-control" ref={sortWrapRef}>
                  <button
                    type="button"
                    className={`sort-trigger ${isSortOpen ? "open" : ""}`}
                    onClick={() => setIsSortOpen(v => !v)}
                    aria-haspopup="menu"
                    aria-expanded={isSortOpen}
                    aria-controls="sort-menu"
                  >
                    <FaSortAmountDownAlt className="sort-icon" />
                    <span className="sort-label">Sort</span>
                    <FaChevronDown className={`chev ${isSortOpen ? "rot" : ""}`} />
                  </button>

                  {isSortOpen && (
                    <div className="sort-menu" id="sort-menu" role="menu">
                      {sortOptions.map((opt) => (
                        <button
                          key={opt.value}
                          role="menuitemradio"
                          aria-checked={sortBy === opt.value}
                          className={`sort-item ${sortBy === opt.value ? "active" : ""}`}
                          onClick={() => {
                            setSortBy(opt.value);
                            setIsSortOpen(false);
                          }}
                        >
                          <span className="item-text">{opt.label}</span>
                          {sortBy === opt.value && <FaCheck className="check" />}
                        </button>
                      ))}

                      <div className="sort-divider" />

                      <button
                        className="sort-dir"
                        onClick={() => setSortDir(d => (d === "asc" ? "desc" : "asc"))}
                        title={sortDir === "asc" ? "Ascending" : "Descending"}
                      >
                        <span>Direction</span>
                        <span className="dir-pill">{sortDir === "asc" ? "ASC" : "DESC"}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          <div className="message-list-items">
            {visibleThreads.length === 0 ? (
              <div className="no-messages-placeholder">
                <img
                  src="/images/no_results_found.png"
                  alt="No results found"
                  className="no-messages-image"
                />
                <p>No Chats found!</p>
              </div>
            ) : (
              <ul>
                {visibleThreads.map(thread => {
                  const {
                    id,
                    client: clientName,
                    worker: workerName,
                    fullMessages = []
                  } = thread;

                  const displayName =
                    userRole === "worker"
                      ? (thread?.client || "Unknown")
                      : (thread?.worker || "Unknown");

                  const safeInitial = (s) =>
                    typeof s === "string" && s.trim().length ? s.trim().charAt(0).toUpperCase() : "U";

                  const { bg, fg } = avatarColorsFor?.(displayName);
                  const [firstName, lastName = ""] = (displayName).split(" ");
                  const lastInitial = lastName ? lastName.charAt(0) : "";
                  const initial = safeInitial(displayName);


                  const visibleFullMessages = fullMessages.filter(m => !isMessageDeleted(m));

                  const lastVisibleMsg =
                    visibleFullMessages[visibleFullMessages.length - 1] || null;

                  const latestMessageTimestamp = Number(lastVisibleMsg?.chatMessageTimeStamp ?? 0);

                  const rawText = lastVisibleMsg?.text || "";
                  const cleanedLatestMessage = rawText
                    ? cleanLatestTextMessage(rawText)
                    : "";

                  const hitInfo = searchHits.get(thread.id);
                  const term = searchTerm.trim();

                  const snippetSource =
                    term && hitInfo?.primary?.text
                      ? hitInfo.primary.text
                      : rawText;

                  const snippetNode = term
                    ? buildHighlightedSnippet(snippetSource, term)
                    : (cleanedLatestMessage && `${cleanedLatestMessage} …`);

                  const unreadCount = unreadCounts[id] || 0

                  return (

                    <li
                      key={thread.id}
                      onClick={() => handleThreadClick(thread)}
                      className={`thread-item ${selectedMessageId === thread.id ? "selected" : ""
                        } ${openThreadMenuId === thread.id ? "menu-open" : ""}`}
                    >
                      {confirmThreadDeleteFor === thread.id && (
                        <div
                          className="inline-confirm"
                          role="alertdialog"
                          aria-labelledby={`confirm-thread-${thread.id}`}
                        >
                          <div className="inline-confirm-content">
                            <span id={`confirm-thread-${thread.id}`} className="inline-confirm-title">
                              Delete thread?
                            </span>
                            <div className="inline-confirm-actions">
                              <button
                                className="btn-danger"
                                disabled={deletingThreadId === thread.id}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setDeletingThreadId(thread.id);
                                  try {
                                    console.log("[PSInbox] Confirmed thread delete:", thread.id);
                                    await softDeleteThread(thread.id, userId);
                                    console.log("✅ Thread deleted:", thread.id);
                                  } catch (err) {
                                    console.error("[PSInbox] Thread delete failed:", err);
                                  } finally {
                                    setDeletingThreadId(null);
                                    setConfirmThreadDeleteFor(null);
                                  }
                                }}
                              >
                                {deletingThreadId === thread.id ? "Deleting…" : "Delete"}
                              </button>

                              <button
                                className="btn-ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmThreadDeleteFor(null);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}


                      <div className="thumbnail-info-actions-wrapper">
                        <div className="thumbnail-info-wrapper">
                          <div
                            className="message-list-thumbnail"
                            style={{ backgroundColor: bg, color: fg }}
                          >
                            {initial}
                          </div>


                          <div className="message-list-info">
                            <div className="message-list-user">
                              <h3>{thread.client}</h3>

                              {unreadCount > 0 && (
                                <span className="unread-count">
                                  {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                              )}

                              <span className="last-message-date">
                                {formatUnixShortDate(latestMessageTimestamp)}
                              </span>
                            </div>
                            <div className="latest-message-pin-wrapper">
                              <div className="latest-message">
                                {snippetNode}
                              </div>
                              {thread.pinned && (
                                <span className="pin-indicator">
                                  <FaThumbtack />
                                </span>
                              )}
                            </div>
                          </div>

                        </div>

                        <div
                          className="thread-actions-control"
                          ref={(el) => actionRefs.current.set(thread.id, el)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className={`thread-actions-trigger ${openThreadMenuId === thread.id ? "open" : ""}`}
                            onClick={(e) => toggleThreadMenu(e, thread.id)}
                            aria-haspopup="menu"
                            aria-expanded={openThreadMenuId === thread.id}
                            aria-controls={`thread-menu-${thread.id}`}
                            title="More options"
                          >
                            <FiMoreVertical />
                          </button>

                          {openThreadMenuId === thread.id && (
                            <div
                              className="thread-actions-menu"
                              id={`thread-menu-${thread.id}`}
                              role="menu"
                            >
                              <button
                                role="menuitem"
                                className={`thread-action-item ${thread.pinned ? "active" : ""}`}
                                onClick={(e) => {
                                  onTogglePinThread(e, thread);
                                  closeThreadMenu();
                                }}
                              >
                                <FaThumbtack />
                                <span>{thread.pinned ? "Unpin" : "Pin"}</span>
                              </button>

                              <button
                                role="menuitem"
                                className={`thread-action-item ${thread.chatArchived ? "active" : ""}`}
                                onClick={(e) => {
                                  onToggleArchiveThread(e, thread);
                                }}
                              >
                                <FiArchive />
                                <span>{thread.chatArchived ? "Unarchive" : "Archive"}</span>
                              </button>

                              <button
                                role="menuitem"
                                className="thread-action-item delete-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenThreadMenuId(null);
                                  setConfirmThreadDeleteFor(thread.id);
                                }}
                              >
                                <FiTrash2 />
                                <span>Delete</span>
                              </button>


                            </div>
                          )}
                        </div>
                      </div>


                    </li>


                  );
                })}
              </ul>

            )}
          </div>
          <button className="chat-fab-btn">
            <FiMessageSquare className="fab-icon" />
            <span className="fab-label">New Chat</span>
          </button>


        </div>
      </div>

      <div
        className="message-content"
        style={{
          display:
            mobileView === "desktop" || mobileView === "content"
              ? undefined
              : "none",
        }}
      >
        <div className="message-content-container">
          {!showContent ? (
            <div className="chat-message-placeholder">
              <div className="message-content-header">
                <h2>Welcome to your Inbox</h2>
              </div>
              <p>Select a message thread to view the conversation.</p>
              <img src="/images/inbox-illustration.png" alt="Inbox Illustration" />
            </div>
          ) : (
            <div className="messageArea">
              <div className="message-content-header">
                <div className="message-back-icon">
                  {mobileView !== "desktop" && showContent && (
                    <FaAngleLeft onClick={handleBackClick} />
                  )}
                </div>
                {selectedThread && (
                  <div className="chat-client-header-container">
                    <div className="avatar-call-options">
                      <div className="avatar-status-wrapper">
                        <div className="client-avatar-name">
                          <img
                            src={clientImageUrl}
                            onError={e => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = defaultAvatar;
                            }}
                            alt="User Avatar"
                            className="client-header-incoming"
                          />
                          <span>{selectedThread.client}</span>
                        </div>
                        <div className="user-status-indicator">
                          <span
                            className={`status-circle ${isOnline ? 'online' : 'offline'}`}
                            title={isOnline ? 'Online' : 'Offline'}
                          ></span>
                          <span className="status-text">{isOnline ? 'Online' : 'Offline'}</span>
                        </div>
                      </div>
                      <div className="calling-options-bar">
                        <button className="calling-option-btn" title="Voice Call">
                          <FaPhoneAlt />
                        </button>
                        <button className="calling-option-btn" title="Video Call">
                          <FaVideo />
                        </button>
                        {mobileView !== "desktop" && (
                          <button onClick={handleInfoClick}>
                            <FaArrowRight /></button>
                        )}
                      </div>
                    </div>
                    <div className="chat-header-container">
                      <div className="job-inbox-title">
                        <span className="job-fa-icon">
                          <FaBriefcase />
                        </span>
                        <span
                          className="job-title-text"
                          onClick={handleJobTitleClick}
                        >
                          {truncateJobTitle(jobTitle, maxTitleLength)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {userId && (
                <div className="inbox-chat-messages-container">
                  {!showScheduler ? (
                    <>
                      <div className="inbox-chat-messages"
                        ref={messagesContainerRef}
                        onScroll={handleScroll}
                        tabIndex={0}
                        onFocus={() => {
                          setIsMsgsFocused(true);
                        }}
                        onBlur={() => setIsMsgsFocused(false)}>
                        {selectedThread && visibleMessages.map((msg, index, array) => {
                          if (isMessageDeleted(msg)) return null;
                          const isOutgoing = msg.sender === userType;
                          let textToRender = msg.text;

                          if (isOutgoing) {
                            textToRender = textToRender.replace(
                              /<p>\s*<button\b[^>]*>\s*Confirm Attendance\s*<\/button>\s*<\/p>/gi,
                              ""
                            );
                          }

                          const isFirstUnread =
                            index ===
                            array.findIndex(
                              (m) => !m.read && m.receiver === userType
                            );

                          const unreadCount = array.filter(
                            m => !m.read && m.receiver === userType
                          ).length;


                          const messageDate = new Date(msg.chatMessageTimeStamp).toDateString();
                          const thisDate = new Date(msg.chatMessageTimeStamp).toDateString();
                          const prevDate = index > 0
                            ? new Date(array[index - 1].chatMessageTimeStamp).toDateString()
                            : null;
                          const shouldShowDateHeader = index === 0 || thisDate !== prevDate;

                          return (
                            <React.Fragment key={index}>
                              {shouldShowDateHeader && (
                                <div className="date-header">
                                  <span className="date-text">
                                    {dateStringWithDayNoTime(msg.chatMessageTimeStamp)}
                                  </span>
                                </div>
                              )}

                              {initialPhaseRef.current && index === firstUnreadIndex && (
                                <div
                                  ref={newMessagesRef}
                                  className="new-message-indicator sticky"
                                >
                                  <MdArrowDownward className="new-msg-icon" />
                                  New
                                </div>
                              )}

                              {!initialPhaseRef.current && incomingAnchorId && msg.messageId === incomingAnchorId && (
                                <div
                                  ref={newMessagesRef}
                                  className="new-message-indicator sticky"
                                >
                                  <MdArrowDownward className="new-msg-icon" />
                                  New
                                </div>
                              )}

                              {index === lastUnreadIndex && (
                                <div ref={lastUnreadRef} style={{ height: '1px' }} />
                              )}


                              <div
                                key={msg.messageId || index}
                                className={`message-time-text-wrapper ${msg.sender === userType ? 'outgoing' : 'incoming'}`}
                              >
                                {msg.sender === userType && (
                                  <div className="message-time-wrapper">
                                    <span className="message-time outgoing-time">
                                      {formatUnixFullTime(msg.chatMessageTimeStamp)}
                                    </span>
                                  </div>
                                )}

                                <div
                                  className={`message-item ${msg.sender === userType ? 'outgoing' : 'incoming'}`}
                                  data-message-id={msg.messageId}
                                  style={{ position: 'relative' }}
                                >

                                  {!msg.read && msg.receiver === userType && <span className="unread-indicator" />}

                                  <div className="message-wrapper">
                                    {msg.pinned && (
                                      <span className="msg-pin-icon">
                                        <FaThumbtack />
                                      </span>
                                    )}


                                    {msg.sender === userType && !msg.messageDeleted && (
                                      <div
                                        className={`msg-actions ${showActionsFor === msg.messageId ? "open" : ""}`}
                                        ref={(el) => {
                                          if (!msg.messageId) return;
                                          if (el) actionRefs.current.set(msg.messageId, el);
                                          else actionRefs.current.delete(msg.messageId);
                                        }}
                                      >

                                        <button
                                          className={`msg-actions-trigger ${showActionsFor === msg.messageId ? "open" : ""}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowActionsFor(prev => (prev === msg.messageId ? null : msg.messageId));
                                          }}
                                          aria-expanded={showActionsFor === msg.messageId}
                                          aria-haspopup="menu"
                                          type="button"
                                          title="Message options"
                                        >
                                          <FaAngleDown />
                                        </button>



                                        {showActionsFor === msg.messageId && (
                                          <div className="msg-actions-menu" role="menu">
                                            <button onClick={() => onTogglePinMessage(msg)} role="menuitem">
                                              <FaThumbtack />
                                              <span>{msg.pinned ? "Unpin" : "Pin"}</span>
                                            </button>
                                            <button onClick={() => startEdit(msg)} role="menuitem">
                                              <CiEdit /> Edit
                                            </button>
                                            <button
                                              onClick={() => {
                                                setShowActionsFor(null);
                                                setConfirmDeleteFor(msg.messageId);
                                              }}
                                              role="menuitem"
                                            >
                                              <FaTrashAlt /> Delete
                                            </button>

                                          </div>

                                        )}
                                      </div>
                                    )}

                                    <div className="message-text-wrapper">
                                      {confirmDeleteFor === msg.messageId && (
                                        <div
                                          className={`inline-confirm ${msg.sender === userType ? "outgoing" : "incoming"}`}
                                          role="alertdialog"
                                          aria-labelledby={`confirm-title-${msg.messageId}`}
                                        >
                                          <div className="inline-confirm-content">
                                            <span id={`confirm-title-${msg.messageId}`} className="inline-confirm-title">
                                              Delete message?
                                            </span>
                                            <div className="inline-confirm-actions">
                                              <button
                                                className="btn-danger"
                                                disabled={deletingMessageId === msg.messageId}
                                                onClick={async () => {
                                                  setDeletingMessageId(msg.messageId);
                                                  try {
                                                    await onSoftDeleteMessage(msg);
                                                  } finally {
                                                    setDeletingMessageId(null);
                                                    setConfirmDeleteFor(null);
                                                  }
                                                }}
                                              >
                                                {deletingMessageId === msg.messageId ? "Deleting…" : "Delete"}
                                              </button>
                                              <button
                                                className="btn-ghost"
                                                onClick={() => setConfirmDeleteFor(null)}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      )}


                                      {!msg.messageDeleted &&
                                        Array.isArray(msg.attachments) &&
                                        msg.attachments.length > 0 && (() => {
                                          const images = msg.attachments.filter(a => ((a.mimeType || a.type) || "").startsWith("image/"));
                                          const others = msg.attachments.filter(a => !((a.mimeType || a.type) || "").startsWith("image/"));
                                          const MAX_VISIBLE_IMAGES = isMobile ? 3 : 5;
                                          const visible = images.slice(0, MAX_VISIBLE_IMAGES);
                                          const hiddenCount = Math.max(0, images.length - MAX_VISIBLE_IMAGES);

                                          return (
                                            <div className="msg-attachments">
                                              {images.length > 0 && (
                                                <div className="att-image-grid">
                                                  {visible.map((a, i) => {
                                                    const href = attUrl(a);
                                                    const alt = a.name || a.fileName || "image";
                                                    return (
                                                      <a key={a.id || i} className="att-image-wrap" href={href} target="_blank" rel="noreferrer" title={alt}>
                                                        <img className="att-image-thumb" src={href} alt={alt} />
                                                      </a>
                                                    );
                                                  })}

                                                  {hiddenCount > 0 && (
                                                    <a
                                                      className="att-image-wrap att-more-tile"
                                                      href={attUrl(images[MAX_VISIBLE_IMAGES])}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      aria-label={`Open ${hiddenCount} more attachment${hiddenCount > 1 ? 's' : ''}`}
                                                      title={`+${hiddenCount} more`}
                                                    >
                                                      <span>+{hiddenCount}</span>
                                                    </a>
                                                  )}
                                                </div>
                                              )}

                                              {others.length > 0 && (
                                                <div className="att-chip-row">
                                                  {others.map((a, i) => (
                                                    <a key={a.id || i} className="msg-attachment-chip" href={attUrl(a)} target="_blank" rel="noreferrer" title={a.name || a.fileName}>
                                                      <span className="att-icon"><FaFileAlt /></span>
                                                      <span className="att-name">{a.name || a.fileName || "file"}</span>
                                                    </a>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}


                                      {editingId === msg.messageId ? (
                                        <div
                                          className="inline-edit"
                                          onKeyDown={(e) => {
                                            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                                              e.preventDefault();
                                              saveEdit(msg);
                                            }
                                            if (e.key === "Escape") {
                                              e.preventDefault();
                                              cancelEdit();
                                            }
                                          }}
                                        >
                                          <InlineHtmlEditor
                                            valueHtml={editDraft}
                                            onChangeHtml={setEditDraft}
                                            className="inline-html-editor"
                                            autofocus
                                            enterAsBr={!wasHtmlRef.current}
                                          />

                                          <div className="inline-edit-actions">
                                            <button
                                              type="button"
                                              className="btn-save"
                                              disabled={!editDraft || !editDraft.trim()}
                                              onPointerUp={(e) => {
                                                e.preventDefault();
                                                if (!editDraft || !editDraft.trim()) return;
                                                saveEdit(msg);
                                              }}
                                            >
                                              Save
                                            </button>

                                            <button
                                              type="button"
                                              className="btn-cancel"
                                              onPointerUp={(e) => {
                                                e.preventDefault();
                                                cancelEdit();
                                              }}
                                            >
                                              Cancel
                                            </button>

                                          </div>
                                        </div>
                                      ) : (

                                        <span className="message-text">
                                          {sanitizeAndParseHtml(textToRender || "")}
                                        </span>

                                      )}
                                      <div className="message-status-wrapper">
                                        <span className="message-time outgoing-time">
                                          {renderMessageStatus(msg)}
                                        </span>
                                      </div>
                                    </div>

                                  </div>
                                </div>

                                {msg.sender !== userType && (
                                  <div className="message-time-wrapper">
                                    <span className="message-time incoming-time">
                                      {formatUnixFullTime(msg.chatMessageTimeStamp)}
                                      {renderMessageStatus(msg)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </React.Fragment>
                          );
                        })}

                        {selectedThread &&
                          isClientReplyToWorker(visibleMessages) &&
                          selectedThread.meetAndGreetSet === false &&
                          selectedThread.workerAllowedToScheduleMeetAndGreet === true && (
                            <div className="inbox-meet-and-greet"
                              ref={meetAndGreetScheduleRef}>
                              <div className="meet-span-wrapper">
                                <span>ℹ️ Meet and Greet Setup</span>

                                <button onClick={() => setShowScheduler(true)}>
                                  Schedule
                                </button>
                              </div>
                              <div className="meet-greet-close">
                                <span onClick={() => handleCloseScheduler()}>
                                  ×
                                </span>
                              </div>
                            </div>
                          )}
                        {canJoinMeetAndGreet && (
                          <div className="inbox-meet-and-greet"
                            ref={meetAndGreetJoinRef}>
                            <div className="meet-span-wrapper">
                              <span>🤝 Meet &amp; Greet time</span>
                              <button onClick={handleJoinMeetAndGreet}>
                                Join Session
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedThread &&
                          selectedThread.meetAndGreetSet &&
                          selectedThread.meetAndGreetConfirmed &&
                          selectedThread.hasClientConfirmedCompletionSuccess &&
                          !selectedThread.hasWorkerConfirmedCompletionSuccess &&
                          !selectedThread.meetAndGreetCompleted && (
                            <div className="inbox-meet-and-greet" ref={confirmOrRescheduleRef}>
                              <div className="meet-span-wrapper">
                                <span>⚠️ Was the Meet &amp; Greet session successful?</span>
                                <button
                                  onClick={() => handleSessionTerminationConfirmation(true)}
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => handleSessionTerminationConfirmation(false)}
                                >
                                  Reschedule
                                </button>
                              </div>
                            </div>
                          )}


                        {canSendAgreement && (
                          <div className="inbox-meet-and-greet"
                            ref={sendAgreementRef}>
                            <div className="meet-span-wrapper">
                              <span className="agreement-helper">
                                {selectedThread.hasClientConfirmedCompletionSuccess &&
                                  selectedThread.hasWorkerConfirmedCompletionSuccess
                                  ? "ℹ️ Confirmed. You can now send the agreement."
                                  : "⏳ Waiting for the other party to confirm."}
                              </span>
                              <button
                                disabled={
                                  selectedThread.hasWorkerConfirmedCompletionSuccess &&
                                  !selectedThread.hasClientConfirmedCompletionSuccess
                                }
                                onClick={navigateToAgreementSetup}
                              >
                                Send Agreement
                              </button>
                            </div>
                          </div>

                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      <div className="chat-input-container">
                        {files.length > 0 && (
                          <div className="pending-attachments">
                            {files.map(({ file, kind, previewUrl }, i) => {
                              const shortName =
                                file.name.length > 26 ? file.name.slice(0, 23) + "..." : file.name;

                              // Generic tile wrapper (used by all kinds)
                              const Tile = ({ children }) => (
                                <div className="attachment-tile">
                                  {children}
                                  <div className="attachment-name-overlay" title={file.name}>
                                    {shortName}
                                  </div>
                                  <button
                                    className="remove-attachment-btn"
                                    onClick={() => removePendingFile(i)}
                                    title="Remove"
                                  >
                                    ×
                                  </button>
                                </div>
                              );

                              if (kind === "image") {
                                return (
                                  <Tile key={i}>
                                    <img
                                      src={previewUrl}
                                      alt={file.name}
                                      className="attachment-thumb"
                                    />
                                  </Tile>
                                );
                              }

                              if (kind === "video") {
                                return (
                                  <Tile key={i}>
                                    <div className="attachment-thumb attachment-thumb--video">
                                      {previewUrl ? (
                                        <a href={previewUrl} target="_blank" rel="noreferrer" className="fill-link" aria-label="Preview video" />
                                      ) : null}
                                      <FaPlay className="overlay-big-icon" />
                                    </div>
                                  </Tile>
                                );
                              }

                              if (kind === "pdf") {
                                return (
                                  <Tile key={i}>
                                    <div className="attachment-thumb attachment-thumb--pdf">
                                      <FaFilePdf className="overlay-big-icon" />
                                      <span className="badge badge-pdf">PDF</span>
                                    </div>
                                  </Tile>
                                );
                              }

                              return (
                                <Tile key={i}>
                                  <div className="attachment-thumb attachment-thumb--file">
                                    <FaFileAlt className="overlay-big-icon" />
                                  </div>
                                </Tile>
                              );
                            })}
                          </div>
                        )}

                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          style={{ display: "none" }}
                          onChange={onFilesPicked}
                        />

                        <div className="chat-input-row">
                          <div className="attach-menu-wrapper">
                            <button
                              className={`chat-attach-button ${showAttachMenu ? "open" : ""}`}
                              onClick={() => setShowAttachMenu((p) => !p)}
                              title={showAttachMenu ? "Close menu" : "Add attachments"}
                              type="button"
                            >
                              {showAttachMenu ? "×" : "+"}
                            </button>


                            {showAttachMenu && (
                              <div className="attach-menu">
                                <button
                                  className="attach-option"
                                  onClick={() => {
                                    setShowAttachMenu(false);
                                    fileInputRef.current?.setAttribute("accept", "image/*");
                                    openFilePicker();
                                  }}
                                >
                                  <IoMdImages className="attach-photo-icon" /> Images
                                </button>
                                <button
                                  className="attach-option"
                                  onClick={() => {
                                    setShowAttachMenu(false);
                                    fileInputRef.current?.removeAttribute("accept");
                                    openFilePicker();
                                  }}
                                >
                                  <FaFileAlt className="attach-document-icon" /> Document
                                </button>
                              </div>
                            )}
                          </div>

                          <TextareaAutosize
                            className="chat-message-input-textarea"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                psSendMessage();
                              }
                            }}
                            placeholder="Type your message..."
                            minRows={1}
                            maxRows={6}
                          />


                          <button className="chat-send-button" onClick={psSendMessage}>
                            <PiPaperPlaneRightFill />
                          </button>
                        </div>

                      </div>

                    </>
                  ) : (
                    <div className="meet-scheduler">
                      <MeetAndGreet
                        userId={userId}
                        userRole={userRole}
                        onClose={() => setShowScheduler(false)}
                        selectedThread={selectedThread}
                        jobTitle={jobTitle}
                        messageId={messageId}
                        jobId={jobId}
                        onScheduleSet={(details) => {
                          setShowScheduler(false);
                          handleScheduleSet(details);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="chat-info-pane"
        style={{
          display:
            mobileView === "desktop" || mobileView === "info"
              ? undefined
              : "none",
        }}>
        <div className="chat-info-header">
          <h2>Chat Settings</h2>
          {mobileView !== "desktop" && (
            <span className="close-info" onClick={handleCloseClick}>X</span>
          )}
        </div>

        {selectedThread && (
          <div className="chat-info-details">
            <div className="user-info">
              <div className="user-circle">
                <img
                  src={clientImageUrl}
                  onError={e => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultAvatar;
                  }}
                  alt="User Avatar"
                  className="avatar"
                />
              </div>
              <div className="chat-info-user">{selectedThread.client}</div>
            </div>

            {isMeetAndGreetSet && isMeetAndGreetConfirmed ? (
              isMeetAndGreetCompleted ? (
                !isAgreementSet ? (
                  <>
                    <div className="agreement-info">
                      <h4>Finalize agreement</h4>
                      <div className="agreement-info-content">
                        <p>
                          To set up care with the client, first agree on the
                          rate and schedule via chat, then offer a formal
                          agreement.
                        </p>
                        <Button
                          className="agreement-info-content-button"
                          onClick={navigateToAgreementSetup}
                        >
                          Send agreement
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="agreement-info">
                    <strong>All steps completed. Agreement set.</strong>
                  </div>
                )
              ) : (
                <div className="agreement-prep-info">
                  <strong>Next step:</strong> {getNextStep()}
                </div>
              )
            ) : (
              <div className="agreement-prep-info">
                <strong>Next step:</strong> {getNextStep()}
              </div>
            )}
          </div>
        )}

        {chatInfoPane && (
          <>
            {/* <ul className="chat-action-list">
              <li className="notification-toggle">
                <span className="icon">🔔</span>
                <div className="toggle-container" onClick={handleToggleNotifications}>
                  <span>
                    {notificationsEnabled
                      ? "Turn Off Notifications"
                      : "Turn On Notifications"}
                  </span>
                  <div
                    className={`toggle-switch ${notificationsEnabled ? "active" : ""
                      }`}
                  >
                    <div className="toggle-circle"></div>
                  </div>
                </div>
              </li>
              <li onClick={() => setShowBlockCallsAlert(true)}>
                <span className="icon">🚫</span>
                Block Calls

                {showBlockCallsAlert && (
                  <BlockCallsAlert
                    isOpen={showBlockCallsAlert}
                    onClose={() => setShowBlockCallsAlert(false)}
                    threadId={selectedThread.id}
                    userId={userId}
                    currentlyBlocked={!!selectedThread?.callsBlocked}
                  />
                )}
              </li>

              <li onClick={() => setShowRestrictAlert(true)}>
                <span className="icon">👤</span>
                Restrict User
                {showRestrictAlert && (
                  <RestrictUserAlert
                    isOpen={showRestrictAlert}
                    onClose={() => setShowRestrictAlert(false)}
                    threadId={selectedThread.id}
                    userId={userId}
                    currentlyRestricted={!!selectedThread?.userBlocked}
                  />
                )}
              </li>
              <li onClick={() => setShowReportAlert(true)}>
                <span className="icon">⚠️</span>
                Report User
                {showReportAlert && (
                  <ReportThreadAlert
                    isOpen={showReportAlert}
                    onClose={() => setShowReportAlert(false)}
                    threadId={selectedThread.id}
                    userId={userId}
                  />
                )}
              </li>
            </ul> */}

            <ul className={`chat-action-list ${!selectedThread ? "disabled" : ""}`}>
              <li
                className={`notification-toggle ${!selectedThread ? "item-disabled" : ""}`}
                onClick={selectedThread ? handleToggleNotifications : undefined}
              >
                <span className={`icon icon-notification`}>
                  {notificationsEnabled ? <HiBellSlash /> : <HiBell />}
                </span>

                <div className="toggle-container">
                  <span>
                    {notificationsEnabled
                      ? "Turn Off Notifications"
                      : "Turn On Notifications"}
                  </span>

                  <div
                    className={`toggle-switch ${notificationsEnabled ? "active" : ""
                      }`}
                  >
                    <div className="toggle-circle"></div>
                  </div>
                </div>
              </li>

              <li
                className={!selectedThread ? "item-disabled" : ""}
                onClick={selectedThread ? () => setShowBlockCallsAlert(true) : undefined}
              >
                <span className="icon icon-block"><FaBan /></span>
                {selectedThread?.callsBlocked ? "Unblock Calls" : "Block Calls"}

                {showBlockCallsAlert && (
                  <BlockCallsAlert
                    isOpen={showBlockCallsAlert}
                    onClose={() => setShowBlockCallsAlert(false)}
                    threadId={selectedThread.id}
                    userId={userId}
                    currentlyBlocked={!!selectedThread?.callsBlocked}
                  />
                )}
              </li>

              <li
                className={!selectedThread ? "item-disabled" : ""}
                onClick={selectedThread ? () => setShowRestrictAlert(true) : undefined}
              >
                <span className="icon icon-restrict"><HiUserMinus /></span>
                {selectedThread?.userBlocked ? "Unrestrict User" : "Restrict User"}

                {showRestrictAlert && (
                  <RestrictUserAlert
                    isOpen={showRestrictAlert}
                    onClose={() => setShowRestrictAlert(false)}
                    threadId={selectedThread.id}
                    userId={userId}
                    currentlyRestricted={!!selectedThread?.userBlocked}
                  />
                )}
              </li>

              <li
                className={!selectedThread ? "item-disabled" : ""}
                onClick={selectedThread ? () => setShowReportAlert(true) : undefined}
              >
                <span className="icon icon-report"><HiFlag /></span>
                Report User

                {showReportAlert && (
                  <ReportThreadAlert
                    isOpen={showReportAlert}
                    onClose={() => setShowReportAlert(false)}
                    threadId={selectedThread.id}
                    userId={userId}
                  />
                )}
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default PSInbox;